/**
 * CRM Async Thunks
 * All async actions for shared CRM operations
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import * as crmApi from '../api/crmApi';

/**
 * CRM Assignees (dedicated crmassignees collection) — full CRUD
 */
export const fetchCRMAssigneesThunk = createAsyncThunk(
  'crm/fetchCRMAssignees',
  async (_, { rejectWithValue }) => {
    try {
      return await crmApi.fetchCRMAssignees();
    } catch (error) {
      console.error('Error fetching CRM assignees:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCRMAssigneeThunk = createAsyncThunk(
  'crm/createCRMAssignee',
  async (form, { rejectWithValue }) => {
    try {
      return await crmApi.createCRMAssignee(form);
    } catch (error) {
      console.error('Error creating CRM assignee:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCRMAssigneeThunk = createAsyncThunk(
  'crm/updateCRMAssignee',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await crmApi.updateCRMAssignee({ id, data });
    } catch (error) {
      console.error('Error updating CRM assignee:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCRMAssigneeThunk = createAsyncThunk(
  'crm/deleteCRMAssignee',
  async (id, { rejectWithValue }) => {
    try {
      await crmApi.deleteCRMAssignee(id);
      return id;
    } catch (error) {
      console.error('Error deleting CRM assignee:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch all assignees
 */
export const fetchAssignees = createAsyncThunk(
  'crm/fetchAssignees',
  async (_, { rejectWithValue }) => {
    try {
      const data = await crmApi.fetchAssignees();
      return data;
    } catch (error) {
      console.error('Error fetching assignees:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Create new assignee
 */
export const createAssigneeThunk = createAsyncThunk(
  'crm/createAssignee',
  async (form, { rejectWithValue }) => {
    try {
      const data = await crmApi.createAssignee(form);
      return data;
    } catch (error) {
      console.error('Error creating assignee:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update assignee
 */
export const updateAssigneeThunk = createAsyncThunk(
  'crm/updateAssignee',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await crmApi.updateAssignee({ id, data });
    } catch (error) {
      console.error('Error updating assignee:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Delete assignee
 */
export const deleteAssigneeThunk = createAsyncThunk(
  'crm/deleteAssignee',
  async (id, { rejectWithValue }) => {
    try {
      const data = await crmApi.deleteAssignee(id);
      return data;
    } catch (error) {
      console.error('Error deleting assignee:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch all tags (assignee tags and lead tags)
 */
export const fetchTagsThunk = createAsyncThunk(
  'crm/fetchTags',
  async (_, { rejectWithValue }) => {
    try {
      const [assigneeTags, leadTags] = await Promise.all([
        crmApi.fetchAssigneeTags(),
        crmApi.fetchLeadTags(),
      ]);
      return { assigneeTags, leadTags };
    } catch (error) {
      console.error('Error fetching tags:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Save assignee tags
 */
export const saveTagsThunk = createAsyncThunk(
  'crm/saveTags',
  async (data, { rejectWithValue }) => {
    try {
      const result = await crmApi.saveAssigneeTags(data);
      return result;
    } catch (error) {
      console.error('Error saving tags:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Save lead tags (and preserve patient tags). Payload: { leadTags, patientTags? }
 */
export const saveLeadTagsThunk = createAsyncThunk(
  'crm/saveLeadTags',
  async ({ leadTags, patientTags = [] }, { rejectWithValue }) => {
    try {
      const result = await crmApi.saveLeadTags({ leadTags, patientTags });
      return { leadTags, raw: result };
    } catch (error) {
      console.error('Error saving lead tags:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch unread counts
 */
export const fetchUnreadCountsThunk = createAsyncThunk(
  'crm/fetchUnreadCounts',
  async (params, { rejectWithValue }) => {
    try {
      const data = await crmApi.fetchUnreadCounts(params);
      return data;
    } catch (error) {
      console.error('Error fetching unread counts:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch pipeline stats
 */
export const fetchPipelineStatsThunk = createAsyncThunk(
  'crm/fetchPipelineStats',
  async (params, { rejectWithValue }) => {
    try {
      const data = await crmApi.fetchPipelineStats(params);
      return data;
    } catch (error) {
      console.error('Error fetching pipeline stats:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch blacklist
 */
export const fetchBlacklistThunk = createAsyncThunk(
  'crm/fetchBlacklist',
  async (_, { rejectWithValue }) => {
    try {
      const data = await crmApi.fetchBlacklist();
      return data;
    } catch (error) {
      console.error('Error fetching blacklist:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Add user to blacklist
 */
export const addToBlacklistThunk = createAsyncThunk(
  'crm/addToBlacklist',
  async (userId, { rejectWithValue }) => {
    try {
      const data = await crmApi.addToBlacklist(userId);
      return data;
    } catch (error) {
      console.error('Error adding to blacklist:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Remove user from blacklist
 */
export const removeFromBlacklistThunk = createAsyncThunk(
  'crm/removeFromBlacklist',
  async (userId, { rejectWithValue }) => {
    try {
      const data = await crmApi.removeFromBlacklist(userId);
      return data;
    } catch (error) {
      console.error('Error removing from blacklist:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch Cognito groups
 */
export const fetchCognitoGroupsThunk = createAsyncThunk(
  'crm/fetchCognitoGroups',
  async (_, { rejectWithValue }) => {
    try {
      const data = await crmApi.fetchCognitoGroups();
      return data;
    } catch (error) {
      console.error('Error fetching Cognito groups:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch merged assignees
 */
export const fetchMergedAssigneesThunk = createAsyncThunk(
  'crm/fetchMergedAssignees',
  async (_, { rejectWithValue }) => {
    try {
      const data = await crmApi.fetchMergedAssignees();
      return data;
    } catch (error) {
      console.error('Error fetching merged assignees:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
